import React, { useState, useEffect } from 'react';
import { tenantApi } from '../services/api';
import TenantCard from '../components/TenantCard';
import Modal from '../components/Modal';

interface TenantFormData {
  name: string;
  slug: string;
  plan: string;
  status: 'active' | 'inactive' | 'suspended';
  midtrans_environment: 'sandbox' | 'production';
  midtrans_server_key: string;
  midtrans_client_key: string;
  email_from: string;
  email_reply_to: string;
  whatsapp_enabled: boolean;
  max_orders_per_month: number;
  max_admin_users: number;
  primary_color: string;
  logo_url: string;
  domain_custom: string;
}

interface TenantsProps {
  selectedTenantId: string | null;
  onSelectTenant: (tenantId: string | null) => void;
}

const Tenants: React.FC<TenantsProps> = ({ selectedTenantId, onSelectTenant }) => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    slug: '',
    plan: 'basic',
    status: 'active',
    midtrans_environment: 'sandbox',
    midtrans_server_key: '',
    midtrans_client_key: '',
    email_from: '',
    email_reply_to: '',
    whatsapp_enabled: false,
    max_orders_per_month: 1000,
    max_admin_users: 3,
    primary_color: '#3b82f6',
    logo_url: '',
    domain_custom: ''
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    if (!selectedTenantId) {
      setSelectedTenant(null);
      return;
    }

    const matchingTenant = tenants.find((tenant) => tenant.id === selectedTenantId) || null;
    setSelectedTenant(matchingTenant);
  }, [selectedTenantId, tenants]);

  const fetchTenants = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tenantApi.getAll();
      setTenants(response.tenants);
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
      setError('Gagal memuat daftar tenant. Silakan coba lagi nanti.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = () => {
    setIsEditMode(false);
    setFormData({
      name: '',
      slug: '',
      plan: 'basic',
      status: 'active',
      midtrans_environment: 'sandbox',
      midtrans_server_key: '',
      midtrans_client_key: '',
      email_from: '',
      email_reply_to: '',
      whatsapp_enabled: false,
      max_orders_per_month: 1000,
      max_admin_users: 3,
      primary_color: '#3b82f6',
      logo_url: '',
      domain_custom: ''
    });
    setIsModalOpen(true);
  };

  const handleEditTenant = (tenant: any) => {
    setIsEditMode(true);
    setFormData({
      name: tenant.name,
      slug: tenant.slug,
      plan: tenant.plan,
      status: tenant.status,
      midtrans_environment: tenant.midtrans_environment,
      midtrans_server_key: tenant.midtrans_server_key,
      midtrans_client_key: tenant.midtrans_client_key,
      email_from: tenant.email_from,
      email_reply_to: tenant.email_reply_to,
      whatsapp_enabled: tenant.whatsapp_enabled,
      max_orders_per_month: tenant.max_orders_per_month,
      max_admin_users: tenant.max_admin_users,
      primary_color: tenant.primary_color,
      logo_url: tenant.logo_url,
      domain_custom: tenant.domain_custom || ''
    });
    setSelectedTenant(tenant);
    setIsModalOpen(true);
  };

  const handleDeleteTenant = async (tenantId: string) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus tenant ini?')) {
      return;
    }

    try {
      await tenantApi.delete(tenantId);
      setTenants(tenants.filter(t => t.id !== tenantId));
      if (selectedTenant?.id === tenantId) {
        setSelectedTenant(null);
        onSelectTenant(null);
      }
    } catch (err) {
      console.error('Failed to delete tenant:', err);
      setError('Gagal menghapus tenant. Silakan coba lagi nanti.');
    }
  };

  const handleSaveTenant = async () => {
    try {
      if (isEditMode && selectedTenant) {
        await tenantApi.update(selectedTenant.id, formData);
        const updatedTenants = tenants.map(t => 
          t.id === selectedTenant.id ? { ...t, ...formData } : t
        );
        setTenants(updatedTenants);
      } else {
        const response = await tenantApi.create(formData);
        setTenants([...tenants, response.tenant]);
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to save tenant:', err);
      setError('Gagal menyimpan tenant. Silakan coba lagi nanti.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Manajemen Tenant</h2>
        <button
          onClick={handleCreateTenant}
          className="bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700"
        >
          + Tambah Tenant
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <h3 className="text-lg font-semibold text-red-900">Error</h3>
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map((tenant) => (
            <div key={tenant.id} className="relative">
              <TenantCard
                tenant={tenant}
                isSelected={selectedTenant?.id === tenant.id || selectedTenantId === tenant.id}
                onClick={() => {
                  setSelectedTenant(tenant);
                  onSelectTenant(tenant.id);
                }}
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleEditTenant(tenant)}
                  className="flex-1 bg-gray-100 text-gray-700 rounded px-3 py-1 text-sm hover:bg-gray-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteTenant(tenant.id)}
                  className="flex-1 bg-red-100 text-red-700 rounded px-3 py-1 text-sm hover:bg-red-200"
                >
                  Hapus
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditMode ? 'Edit Tenant' : 'Tambah Tenant Baru'}
      >
        <form onSubmit={(e) => { e.preventDefault(); handleSaveTenant(); }} className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-scroll overscroll-contain px-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nama Tenant</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
                required
                placeholder="contoh: buatfilm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <select
                name="plan"
                value={formData.plan}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="basic">Basic</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Midtrans Environment</label>
              <select
                name="midtrans_environment"
                value={formData.midtrans_environment}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Midtrans Server Key</label>
              <input
                type="password"
                name="midtrans_server_key"
                value={formData.midtrans_server_key}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Midtrans Client Key</label>
              <input
                type="password"
                name="midtrans_client_key"
                value={formData.midtrans_client_key}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email From</label>
              <input
                type="email"
                name="email_from"
                value={formData.email_from}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Reply To</label>
              <input
                type="email"
                name="email_reply_to"
                value={formData.email_reply_to}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="whatsapp_enabled"
                  checked={formData.whatsapp_enabled}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Enable WhatsApp Notifications</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Orders Per Month</label>
              <input
                type="number"
                name="max_orders_per_month"
                value={formData.max_orders_per_month}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Admin Users</label>
              <input
                type="number"
                name="max_admin_users"
                value={formData.max_admin_users}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
              <input
                type="color"
                name="primary_color"
                value={formData.primary_color}
                onChange={handleInputChange}
                className="w-full h-10 border rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
              <input
                type="url"
                name="logo_url"
                value={formData.logo_url}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom Domain</label>
              <input
                type="text"
                name="domain_custom"
                value={formData.domain_custom}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="contoh: buatfilm.com"
              />
            </div>
          </div>

          <div className="sticky bottom-0 flex gap-2 border-t bg-white px-4 py-4">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="flex-1 bg-gray-200 text-gray-700 rounded-lg px-4 py-2 hover:bg-gray-300"
            >
              Batal
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700"
            >
              {isEditMode ? 'Update' : 'Simpan'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Tenants;
